<template>
  <v-sheet class="mt-5 pa-5" tile>
    <v-card>
      <l-map
        ref="map"
        :zoom.sync="zoom"
        :options="mapOptions"
        :center="center"
        :bounds="bounds"
        :min-zoom="minZoom"
        :max-zoom="maxZoom"
        style="z-index: 2; height: 500px; width: 100%"
      >
        <l-tile-layer
          v-for="tileProvider in tileProviders"
          :key="tileProvider.name"
          :name="tileProvider.name"
          :visible="tileProvider.visible"
          :url="tileProvider.url"
          :attribution="tileProvider.attribution"
          layer-type="base"
        />
        <l-control-zoom :position="zoomPosition" />
        <l-control-attribution
          :position="attributionPosition"
          :prefix="attributionPrefix"
        />
        <l-control-scale :imperial="imperial" />
        <v-marker-cluster>
          <l-marker
            v-for="marker in buildingComplexes"
            :key="marker.id"
            :ref="`marker_${marker.id}`"
            :visible="marker.visible"
            :draggable="marker.draggable"
            :lat-lng.sync="marker.position"
            @mouseover="customTip(marker)"
            @mouseout="removeCustomTip(marker)"
          >
            <l-popup>
              <p class="font-weight-bold">
                {{ marker.tooltip.name }}
                <router-link
                  class="text-decoration-none"
                  target="_blank"
                  :to="{
                    name: 'building_complexes.show',
                    params: { id: marker.id }
                  }"
                >
                  <v-icon small>mdi-eye-outline</v-icon>
                </router-link>
              </p>
              <p>{{ $t('BUILDINGS.ITEMS') }}: {{ marker.popup.buildings }}</p>
              <p>{{ $t('APARTMENTS.ITEMS') }}: {{ marker.popup.apartments }}</p>
              <p>
                {{ $t('APARTMENTS.FREE_APARTMENTS') }}:
                {{ marker.popup.empty_apartments }}
              </p>
              <p>
                {{ $t('INHABITANTS.ITEMS') }}: {{ marker.popup.inhabitants }}
              </p>
            </l-popup>

            <l-icon :icon-anchor="staticAnchor">
              <v-icon x-large :color="marker.color"
                >mdi-map-marker-outline</v-icon
              >
            </l-icon>
          </l-marker>
        </v-marker-cluster>
      </l-map>
    </v-card>
  </v-sheet>
</template>

<script>
import { latLngBounds } from 'leaflet';
import {
  LMap,
  LTileLayer,
  LMarker,
  LPopup,
  LControlZoom,
  LControlAttribution,
  LControlScale,
  LIcon
} from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';

import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

import L from 'leaflet';
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster';
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

const tileProviders = [
  {
    name: 'OpenStreetMap',
    visible: true,
    attribution:
      '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
  },
  {
    name: 'OpenTopoMap',
    visible: false,
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution:
      'Map data: &copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)'
  }
];

export default {
  name: 'DashboardMap',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LControlZoom,
    LControlAttribution,
    LControlScale,
    LIcon,
    'v-marker-cluster': Vue2LeafletMarkerCluster
  },

  props: {
    buildingComplexes: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      center: [48.48, 8.13],
      mapOptions: {
        zoomControl: false,
        attributionControl: false,
        zoomSnap: true
      },
      zoom: 7,
      minZoom: 1,
      maxZoom: 20,
      zoomPosition: 'topleft',
      attributionPosition: 'bottomright',
      attributionPrefix: 'Vue2Leaflet',
      imperial: false,

      tileProviders: tileProviders,
      bounds: latLngBounds(
        { lat: 46.29381556233369, lng: 6.405029296875001 },
        { lat: 47.34626718205302, lng: 10.469970703125002 }
      ),
      staticAnchor: [16, 37]
    };
  },

  methods: {
    tooltipContent(marker) {
      return `<p class="font-weight-bold">${marker.tooltip.name}</p>
              <p>${marker.tooltip.b_customer || ''}</p>
              <p>${marker.tooltip.concierge || ''}</p>
              <p>${marker.tooltip.zip || ''}, ${marker.tooltip.city || ''}</p>`;
    },

    customTip(markerData) {
      const markerRef = this.$refs[`marker_${markerData.id}`];
      if (!markerRef || !markerRef[0]) return;
      const marker = markerRef[0];
      const map = marker.mapObject._map;

      const calcTooltipOptions = () => {
        const markerLatLng = marker.mapObject.getLatLng();
        const point = map.latLngToContainerPoint(markerLatLng);
        const mapSize = map.getSize();

        const distances = {
          top: point.y,
          bottom: mapSize.y - point.y,
          left: point.x,
          right: mapSize.x - point.x
        };
        const closestSide = Object.keys(distances).reduce((a, b) =>
          distances[a] < distances[b] ? a : b
        );
        const direction = {
          top: 'bottom',
          bottom: 'top',
          left: 'right',
          right: 'left'
        }[closestSide];

        let offset;
        switch (direction) {
          case 'top':
            offset = L.point(0, -20);
            break;
          case 'bottom':
            offset = L.point(0, 20);
            break;
          case 'left':
            offset = L.point(-20, 0);
            break;
          case 'right':
            offset = L.point(20, 0);
            break;
          default:
            offset = L.point(0, -20);
        }
        return { direction, offset };
      };

      const { direction, offset } = calcTooltipOptions();
      marker.mapObject.unbindTooltip();
      marker.mapObject
        .bindTooltip(this.tooltipContent(markerData), {
          direction: direction,
          interactive: true,
          offset: offset
        })
        .openTooltip();

      if (marker.moveHandler) {
        map.off('move', marker.moveHandler);
      }

      const moveHandler = () => {
        if (marker.mapObject.isTooltipOpen()) {
          const { direction, offset } = calcTooltipOptions();
          marker.mapObject.unbindTooltip();
          marker.mapObject
            .bindTooltip(this.tooltipContent(markerData), {
              direction: direction,
              interactive: true,
              offset: offset
            })
            .openTooltip();
        }
      };

      map.on('move', moveHandler);
      marker.moveHandler = moveHandler;
    },

    removeCustomTip(markerData) {
      const markerRef = this.$refs[`marker_${markerData.id}`];
      if (!markerRef || !markerRef[0]) return;
      const marker = markerRef[0];
      const map = marker.mapObject._map;
      if (marker.moveHandler) {
        map.off('move', marker.moveHandler);
        marker.moveHandler = null;
      }
      marker.mapObject.unbindTooltip();
    }
  }
};
</script>

<style scoped>
.leaflet-tooltip-pane {
  overflow: visible !important;
}
</style>
